import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { MergedRoute } from '@next/core-lib/router-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterNavigationAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { Barn, UpsertBarnResponse } from '../../models/barn.model';
import { SettingModuleState } from '../../models/setting.model';
import { BarnService } from '../../services/barn.service';
import * as settingActions from '../actions/setting.actions';
import { getSelectedBarn, getSelectedBarnId } from '../selectors/setting.selector';

@Injectable()
export class SettingEffects {
  constructor(
    private actions$: Actions,
    private store: Store<SettingModuleState>,
    private router: Router,
    private ngZone: NgZone,
    private barnService: BarnService,
    private dialogService: DialogService,
    @Inject(LocaleService) private locale: LocaleService,
  ) {}

  loadBarns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadBarnsRequest),
      mergeMap(() =>
        this.barnService.getBarns().pipe(
          withLatestFrom(this.store.select(getSelectedBarnId)),
          switchMap(([barns, barnId]: [Barn[], number]) =>
            of(
              settingActions.loadBarnsSuccess({ barns }),
              settingActions.setSelectedBarn({ barnId }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadBarnsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteBarnRequest),
      mergeMap(({ barn }) =>
        this.barnService.deleteBarn(barn.id).pipe(
          map(() => settingActions.deleteBarnSuccess({ barn })),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.deleteBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveSelectedBarn$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertBarn),
      switchMap((_) => this.store.pipe(select(getSelectedBarn), take(1))),
      map((barn: Barn) => settingActions.upsertBarnRequest({ barn })),
    ),
  );

  upsertBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertBarnRequest),
      exhaustMap(({ barn }) =>
        this.barnService.upsertBarn(barn).pipe(
          map((response: UpsertBarnResponse) =>
            settingActions.upsertBarnSuccess({
              barn: {
                ...barn,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.upsertBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  changeNavigationUrl$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map(
        (action: RouterNavigationAction<MergedRoute>) =>
          action.payload.routerState.params['barnId'],
      ),
      map((barnId) => settingActions.setSelectedBarn({ barnId: parseInt(barnId, 10) })),
    ),
  );

  upsertBarnRequestSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.upsertBarnSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/settings', 'barn'])).then();
        }),
      ),
    { dispatch: false },
  );

  deleteBarnWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteBarnWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('barn-item')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                settingActions.deleteBarnRequest({
                  barn: action.barn,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  private getDeleteDialogConfimation(type: string) {
    return {
      text: this.locale.translate(`barn.delete-item-${type}`),
    };
  }
}
